export type Lang = "uk" | "en";

export interface Dict {
  nav: { home: string; about: string; requests: string; services: string; blog: string };
  hero: {
    eyebrow: string;
    name: string;
    tagline: string;
    cta: string;
    method: string;
    badge: string;
  };
  reasons: {
    quote: string;
    eyebrow: string;
    titleLead: string;
    titleAccent: string;
    priceCta: string;
    items: { n: string; t: string; d: string }[];
  };
  about: {
    eyebrow: string;
    titleLead: string;
    titleAccent: string;
    bio: string;
    cta: string;
    yearsN: string;
    yearsL: string;
    clientsN: string;
    clientsL: string;
    quote: string;
  };
  aboutPage: {
    badge: string;
    lede: string;
    essayEyebrow: string;
    essayTitleLead: string;
    essayTitleAccent: string;
    essayBody: string[];
    eduEyebrow: string;
    eduItems: { t: string; d: string }[];
    membersEyebrow: string;
    members: { sh: string; f: string; href?: string }[];
    confEyebrow: string;
    confLede: string;
    confItems: string[];
    ctaTitleLead: string;
    ctaTitleAccent: string;
    ctaBtn: string;
    pageTitle: string;
  };
  consult: {
    eyebrow: string;
    titleLead: string;
    titleAccent: string;
    body: string;
    cta: string;
    contactsEyebrow: string;
  };
  footer: {
    tag: string;
    titleLead: string;
    titleAccent: string;
    cta: string;
    address: string;
    legal: { contacts: string; offer: string; rules: string };
    role: string;
  };
  services: {
    eyebrow: string;
    titleLead: string;
    titleAccent: string;
    lede: string;
    tiersEyebrow: string;
    tiers: {
      tag: string;
      name: string;
      price: string;
      dur: string;
      d: string;
      inc: string[];
      featured?: boolean;
      ctaLabel?: string;
      ctaHref?: string;
      /** If set, the phrase inside `d` is rendered as an inline link, and the bottom CTA is hidden. */
      inlineLink?: { phrase: string; href: string };
    }[];
    whenEyebrow: string;
    whenTitleLead: string;
    whenTitleAccent: string;
    whenItems: string[];
    formatEyebrow: string;
    formatTitleLead: string;
    formatTitleAccent: string;
    format: { n: string; t: string; d: string }[];
    cancellationEyebrow: string;
    cancellation: string;
    faqEyebrow: string;
    faqTitleLead: string;
    faqTitleAccent: string;
    faqItems: { q: string; a: string }[];
    ctaBtn: string;
    pageTitle: string;
  };
  requests: {
    eyebrow: string;
    titleLead: string;
    titleAccent: string;
    items: string[];
    ctaBtn: string;
    pageTitle: string;
  };
  blog: {
    eyebrow: string;
    titleLead: string;
    titleAccent: string;
    lede: string;
    fresh: string;
    readMore: string;
    minutes: string;
  };
  pageTitles: { blog: string };
}

const REASONS_UK = [
  { n: "01", t: "Тривога", d: "" },
  { n: "02", t: "Депресія", d: "" },
  { n: "03", t: "Труднощі в стосунках", d: "" },
  { n: "04", t: "Психосоматичні розлади", d: "" },
  { n: "05", t: "Розлади особистості", d: "" },
  { n: "06", t: "Самооцінка", d: "" },
  { n: "07", t: "Втрати і кризи", d: "" },
];

const REASONS_EN = [
  { n: "01", t: "Anxiety", d: "Intrusive thoughts, restlessness, trouble sleeping." },
  { n: "02", t: "Depression", d: "Fatigue, flatness, loss of meaning and interest." },
  { n: "03", t: "Relationships", d: "Partners, family, friendships — closeness and distance." },
  { n: "04", t: "Psychosomatics", d: "When the body carries what words cannot yet hold." },
  { n: "05", t: "Self-esteem", d: "Doubt, perfectionism, fear of mistake." },
  { n: "06", t: "Loss & crises", d: "Grief, turning points, living through war." },
];

export const DICT: Record<Lang, Dict> = {
  uk: {
    nav: { home: "Головна", about: "Про мене", requests: "Запити", services: "Послуги", blog: "Блог" },
    hero: {
      eyebrow: "Психолог, психотерапевт",
      name: "Наталія Дем'янюк",
      tagline:
        "Вітаю! Я сертифікована психотерапевтка, психологиня, лекторка.\n\nЗ особистого та професійного досвіду знаю, що психотерапія дарує можливість краще розуміти себе, знайти вихід з життєвих труднощів, а також будувати стосунки, в яких хочеться бути.\n\nЯ тут, щоб супроводжувати вас на цьому шляху.",
      cta: "Записатись на консультацію",
      method: "",
      badge: "",
    },
    reasons: {
      quote: "",
      eyebrow: "Послуги",
      titleLead: "Запити",
      titleAccent: "",
      priceCta: "Дізнатись вартість",
      items: REASONS_UK,
    },
    about: {
      eyebrow: "Про мене",
      titleLead: "",
      titleAccent: "",
      bio: "",
      cta: "Детальніше",
      yearsN: "",
      yearsL: "",
      clientsN: "",
      clientsL: "",
      quote: "",
    },
    aboutPage: {
      badge: "",
      lede: "",
      essayEyebrow: "",
      essayTitleLead: "",
      essayTitleAccent: "",
      essayBody: [
        "Вітаю! Я - Наталя Дем'янюк, магістр психології та сертифікована психотерапевтка в методі символдрами (кататимно-імагінативної психотерапії), членкиня УСП, НПА та ІРСГП",
        "Мій досвід приватної практики розпочався у 2019 році. З того часу супроводжую в терапевтичному процесі клієнтів з різних куточків світу через різноманітні життєві обставини до їхніх бажаних змін та зростань. Це відповідальний, трепетний та творчий процес.",
        "Для того, щоб бути корисною у цьому, я здобула відповідну освіту, пройшла власну індивідуальну та групову психотерапії; маю регулярні супервізії; беру участь в навчальних семінарах та міжнародних конференціях.",
        "Вважаю, що психотерапія - це особливі стосунки, в яких розкривається ваша унікальна історія життя, для того, щоб зрозуміти її, осмислити та внести альтернативний сценарій. Ключову роль у цих стосунках відіграють чесність та довіра.",
        "Зважившись на психотерапію, ви відкриєте для себе можливість глибшого розуміння себе та інших, краще долатимете життєві труднощі. Як результат, поліпшиться якість вашого життя. Це нелегкий процес, але вартує того.",
      ],
      eduEyebrow: "Моя освіта",
      eduItems: [
        {
          t: "",
          d: "Український католицький університет, психологія з основами психодинамічної терапії, магістр психології",
        },
        {
          t: "",
          d: "Інститут розвитку символдрами та глибинної психології, психотерапевтка в методі кататимно-імагінативної психотерапії",
        },
        {
          t: "",
          d: "Пʼятирічний міжнародний проєкт з групової психотерапії «Трускавецька школа психотерапії», напрямок групового аналізу, 2026 р. — триває",
        },
      ],
      membersEyebrow: "",
      members: [],
      confEyebrow: "Додаткові семінари, конференції",
      confLede: "",
      confItems: [
        "Теоретико-супервізійний семінар, 3-4 липня 2020 р., д.мед.н., психіатр, психоаналітик, сертифікований супервізор, Голова правління і доцент DGKB Ульріх Заксе, 6 год.",
        "I Міжнародна науково-практична конференція “Символдрама: ремесло і мистецтво”, 10-13 вересня 2020 р., д.мед.н, психоаналітик, доцент і супервізор IGKIP Гаральд Ульман, 30 год.",
        "Міжнародний науково-практичний семінар “Конфронтація травми методом символдрами”, 12-13 березня 2021 р., д.мед.н., психіатр, психоаналітик, сертифікований супервізор, Голова правління і доцент DGKB Ульріх Заксе, 20 год.",
        "II Міжнародна науково-практична конференція “Любовна інтенція в символдрамі”, 16-19 вересня 2021 р., д.ф.н., клінічна психологиня, навчальна терапевтка IGKIP, докторка психотерапевтичних наук в Альпен-Адріа-Університет Трауде Еберман, 30 год.",
        "Психоаналітичний онлайн-воркшоп Ненсі МакВільямс, 18 грудня 2021 р., 10 год",
        "XXVIII науково-практична конференція «Психотерапевт і війна: зустріч на межі реальностей», УСП, 18-19 червня 2022 р., 16 год",
        "III Міжнародна науково-практична конференція «Символдрама сьогодення: Війна і Життя на шляху до Миру», 15-18 вересня, 2022 р., 30 год., доцент IGKIP Клаудіус Штайн",
        "IX Науково-практична конференція \"Війна. Травма. Ідентичність\", УКПП, 3-4 грудня 2022 р.",
        "“Як працювати з різними розладами особистості в нашій клінічній практиці”, доктор Отто Кернберг, 27-28 січня та 10-11 лютого 2023 р., 8 год",
        "IV Міжнародна науково-практична конференція “Час Відродження: терапевтичні можливості символдрами”, 14-17 вересня 2023 р., доцент IGKIP Гудрун Хаммон, 30 год.",
        "Семінар “Психоаналіз мистецтва та мистецтво в психоаналізі”, 22-26 червня 2024 р., Відень, канд.псих.наук, супервізорка ЄКПП, УСП Юлія Мединська, канд.псих.наук, психотерапевтка ЄКПП Ольга Яскевич, 30 год. теорії, 6 год. супервізії",
        "“Психосоматика в неврології. Психосоматичний погляд на обсесивно-компульсивний розлад особистості”, проф. д-р Пауль Янссен, проф.д-р Ульріх Шульц-Венрат, 8-9 червня 2024 р., 8 год",
        "“Доказова практика в психології та психотерапії”, 7 вересня 2024 р., канд.псих.наук, клінічний психолог, психоаналітик Мар’яна Великодна, 4 год",
        "V Міжнародна науково-практична конференція “Кататимно-імагінативна психодинамічна психотерапія і виклики сьогодення”, 12-15 вересня 2024 р., доцент IGKIP Андреа Фрідріхс-Дахале, 30 годин",
        "Теоретико-практичний семінар з вивчення операціоналізованої психодинамічної діагностики ОПД-3, 10-12 жовтня 2024 р., 24 год",
        "Міжнародна науково практична конференція “Війна і виснаження. Психологія опору і відновлення”, УКУ, 18-19 жовтня 2024 р.",
        "Науково-практичний семінар “Символдрама і сучасний психоаналіз: жіноча перспектива”, 2-3 листопада 2024 р., д.ф.н., клінічна психологиня, супервізорка IGKIP Трауде Еберман, 12 год.",
        "“Розлади особистості та психосоматика”, проф. д-р Пауль Янссен, д-р мед. Катаріна Мартін, 8-9 березня 2025 р., 8 год.",
        "“Сім’я як родинний оберіг у сучасному світі”, 6 вересня 2025 р., доцентр ІРСГП, супервізор УСП, системний сімейний терапевт Дорофєєва Л.Г., 10 год.",
        "Науково практичні семінари “Робота з травмою в Кататимно-імагінативній психотерапії”, психотерапевт, психоаналітик, доцент і супервізор Німецького товариства КІП Беате Штайнер, жовтень 2025 р. - березень 2026 р.",
      ],
      ctaTitleLead: "",
      ctaTitleAccent: "",
      ctaBtn: "Записатись на консультацію",
      pageTitle: "Про мене",
    },
    consult: {
      eyebrow: "Консультації",
      titleLead: "",
      titleAccent: "",
      body: "",
      cta: "Записатись на консультацію",
      contactsEyebrow: "Контакти",
    },
    footer: {
      tag: "",
      titleLead: "",
      titleAccent: "",
      cta: "",
      address: "",
      legal: { contacts: "Контакти", offer: "Публічна оферта", rules: "Умови консультування" },
      role: "Психолог, психотерапевт",
    },
    services: {
      eyebrow: "Послуги",
      titleLead: "",
      titleAccent: "",
      lede:
        "Консультація психолога та психотерапія у Львові та онлайн.\n\nВартість консультації формується на основі сертифікації та досвіду. Фіксується на рік з 1 вересня по 31 серпня.",
      tiersEyebrow: "",
      tiers: [
        {
          tag: "",
          name: "Індивідуальна консультація / психотерапія",
          price: "2000 грн",
          dur: "Тривалість: 50 хвилин",
          d: "",
          inc: [],
          featured: true,
        },
        {
          tag: "Соціальна вартість",
          name: "Студенти, військовослужбовці та їхні сім'ї, ВПО",
          price: "",
          dur: "",
          d: "Якщо це актуально для вас",
          inc: [],
          ctaLabel: "просто напишіть мені",
          ctaHref: "https://t.me/taliademianiuk",
        },
        {
          tag: "",
          name: "Проведення лекцій, написання статей",
          price: "",
          dur: "",
          d: "Окрім індивідуальної роботи працюю лекторкою в темі психічного здоров'я, а також на перетині глибинної психології та мистецтва.\n\nМаю любов до написання текстів, а перша освіта в галузі журналістики дозволяє знаходити доволі точні слова і форму.\n\nВартість послуг обговорюється індивідуально.",
          inc: [],
          ctaLabel: "Запрошую до контакту",
          ctaHref: "/#contacts",
        },
      ],
      whenEyebrow: "Коли варто звернутись до психолога, психотерапевта за консультацією",
      whenTitleLead: "",
      whenTitleAccent: "",
      whenItems: [
        "Ви стали гірше справлятись емоційно: часто змінюється настрій, з’явилась постійна дратівливість, або ж ви почали відчувати безпричинний сум; відчуваєте тривогу, що заважає в повсякденному житті, апатію чи пригнічення.",
        "З’явилось відчуття емоційної втоми, коли енергії вистачає тільки на “виживання”.",
        "Відчуваєте тілесні симптоми, які не мають медичного пояснення, та/або які посилюються при стресі.",
        "Маєте важкий період у стосунках; стосунки стали більше забирати вашої енергії й сил, що негативно впливає на якість вашого життя.",
        "Відчуття нерозуміння та конфлікти з близькими.",
        "Труднощі в побудові та підтримці стосунків.",
        "Відчуття самотності, що не зникає навіть серед людей.",
        "Відчуття розгубленості, нерозуміння своїх бажань і потреб, часті сумніви у життєвих виборах.",
        "Здається, що ви живете не своїм життям, часті порівняння з іншими.",
        "Сумніви у собі, у власних досягненнях, відчуття, що \"недостатньо хороший / хороша\".",
        "Є відчуття, що щось не так, попри те, що ззовні виглядає все добре.",
        "Переживання втрати, життєвої кризи, екзистенційних питань.",
        "Бажання жити більш усвідомлено, покращити якість життя.",
      ],
      formatEyebrow: "",
      formatTitleLead: "",
      formatTitleAccent: "",
      format: [],
      cancellationEyebrow: "",
      cancellation: "",
      faqEyebrow: "FAQ",
      faqTitleLead: "Часті запитання",
      faqTitleAccent: "",
      faqItems: [
        {
          q: "Який формат роботи ви пропонуєте?",
          a: "Можливий формат консультування: кілька зустрічей для вирішення поточних питань, а також формат психотерапії: тривала робота для глибоких і стійких змін.",
        },
        {
          q: "Яка тривалість і частота зустрічей?",
          a: "Тривалість консультації — 50 хв, 1 або 2 рази в тиждень.",
        },
        {
          q: "Як виглядає перша зустріч?",
          a: "Перша зустріч проходить у форматі розмови, де ми можемо познайомитись і налагодити контакт, окреслити запит та дослідити контекст вашого життя. На першій зустрічі ви також можете зрозуміти, чи комфортно вам працювати разом.",
        },
        {
          q: "Які є способи оплати?",
          a: "Оплата здійснюється за офіційними реквізитами, до чи після консультації.\n\nЗдійснити оплату з-за кордону можна без комісій за допомогою платіжної системи WayForPay.",
        },
        {
          q: "Як бути, якщо я хочу перенести або скасувати зустріч?",
          a: "Перенесення або скасування зустрічі можливе при попередженні за добу. В іншому випадку, така зустріч оплачується в повному розмірі.\n\nЯкщо зустріч скасовується або переноситься менш, ніж за добу з боку терапевта, — наступна зустріч буде для вас безкоштовна.",
        },
        {
          q: "Якщо в якийсь момент мені не захочеться йти на зустріч?",
          a: "Зазвичай, за таким небажанням ховаються різні емоції (гнів, сором, провина, безсилля) повʼязані з процесом терапії. Важлива частина психотерапевтичної роботи — називати та обговорювати їх, замість того, щоб уникати.",
        },
        {
          q: "Якщо в якийсь момент я захочу завершити терапію?",
          a: "Варто виділити від 1 до 3 зустрічей, щоб підсумувати роботу і завершити співпрацю.",
        },
      ],
      ctaBtn: "Записатись на консультацію",
      pageTitle: "Ціни",
    },
    requests: {
      eyebrow: "Запити",
      titleLead: "Коли варто звернутись до психолога,",
      titleAccent: "психотерапевта за консультацією.",
      items: [
        "Ви стали гірше справлятись емоційно: часто змінюється настрій, з’явилась постійна дратівливість, або ж ви почали відчувати безпричинний сум; відчуваєте тривогу, що заважає в повсякденному житті, провину, апатію чи пригнічення.",
        "З’явилось відчуття емоційної втоми, коли енергії вистачає тільки на “виживання”.",
        "Відчуваєте тілесні симптоми, які не мають медичного пояснення, та/або які посилюються при стресі.",
        "Маєте важкий період у стосунках; стосунки стали більше забирати вашої енергії й сил, що негативно впливає на якість вашого життя.",
        "Відчуття нерозуміння та конфлікти з близькими.",
        "Труднощі в побудові та підтримці стосунків.",
        "Відчуття самотності, що не зникає навіть серед людей.",
        "Відчуття розгубленості, нерозуміння своїх бажань і потреб, часті сумніви у життєвих виборах.",
        "Здається, що ви живете не своїм життям, часті порівняння з іншими.",
        "Сумніви у собі, у власних досягненнях, відчуття, що \"недостатньо хороший / хороша\".",
        "Є відчуття, що щось не так, попри те, що ззовні виглядає все добре.",
        "Переживання втрати, життєвої кризи, екзистенційних питань, змін.",
        "Бажання жити більш усвідомлено, покращити якість життя.",
      ],
      ctaBtn: "Записатись на консультацію",
      pageTitle: "Запити",
    },
    blog: {
      eyebrow: "Блог",
      titleLead: "",
      titleAccent: "",
      lede: "",
      fresh: "",
      readMore: "",
      minutes: "хв",
    },
    pageTitles: { blog: "Блог" },
  },
  en: {
    nav: { home: "Home", about: "About", requests: "Requests", services: "Services", blog: "Journal" },
    hero: {
      eyebrow: "Psychologist · Psychotherapist",
      name: "Nataliia Demianiuk",
      tagline:
        "Psychotherapy is a way back to yourself — to what hurts, to what brings joy, and to the relationships you want to be in.",
      cta: "Book a consultation",
      method: "Symboldrama method\n(katathym-imaginative therapy)",
      badge: "Lviv · UA",
    },
    reasons: {
      quote:
        "In a safe, unhurried space, we explore what hurts — and find a way through together.",
      eyebrow: "What people come with",
      titleLead: "Requests we",
      titleAccent: "work with.",
      priceCta: "See pricing",
      items: REASONS_EN,
    },
    about: {
      eyebrow: "About me",
      titleLead: "Walking alongside",
      titleAccent: "those who look for themselves.",
      bio:
        "Master in psychology, Ukrainian Catholic University. Certified psychotherapist in the symboldrama method. Teaching assistant at UCU's department of psychology and psychotherapy. Practicing in Lviv and online since 2019.",
      cta: "Full biography",
      yearsN: "6",
      yearsL: "years of practice",
      clientsN: "120+",
      clientsL: "people accompanied",
      quote: "“I don't fix. I accompany a process that is yours.”",
    },
    aboutPage: {
      badge: "Lviv · UA",
      lede:
        "I work with adults who come for support in difficult moments, in search of meaning, or in the wish to know themselves more deeply. My work is to be an attentive witness to your process.",
      essayEyebrow: "How I see therapy",
      essayTitleLead: "Therapy is",
      essayTitleAccent: "not «fixing».",
      essayBody: [
        "Hello — I'm Nataliia Demianiuk: master in psychology and certified psychotherapist in the symboldrama method (katathym-imaginative psychotherapy), member of USP, NPA and IRSGP.",
        "My private practice began in 2019. Since then I have accompanied clients from across the world through a wide range of life circumstances toward the changes and growth they were seeking. It is responsible, tender and deeply creative work.",
        "To be of use in this, I completed the relevant education, went through my own individual and group psychotherapy, and I keep regular supervision and take part in training seminars and international conferences.",
        "I believe psychotherapy is a particular kind of relationship — one in which your unique life story unfolds so that it can be understood, made sense of, and rewritten. Honesty and trust play a key role in this relationship.",
        "When you decide on psychotherapy, you open up the possibility of a deeper understanding of yourself and others, and become better at meeting life's difficulties. As a result, the quality of your life improves. The process is not easy, but it is worth it.",
      ],
      eduEyebrow: "Education",
      eduItems: [
        {
          t: "Master in Psychology",
          d: "Ukrainian Catholic University — psychology with foundations of psychodynamic therapy.",
        },
        {
          t: "Psychotherapist",
          d: "Institute for the Development of Symboldrama and Deep Psychology — katathym-imaginative method.",
        },
      ],
      membersEyebrow: "Memberships",
      members: [
        {
          sh: "USP",
          f: "Ukrainian Society of Psychotherapists",
          href: "https://usp.community/cells/12-cells/92-lvivskyi-oseredok",
        },
        {
          sh: "NPA",
          f: "National Psychological Association",
          href: "https://npa-ua.org/register/demyanyuk-nataliya-vasylivna-4794/",
        },
        {
          sh: "IRSGP",
          f: "Institute for the Development of Symboldrama and Deep Psychology",
          href: "https://symboldrama.com.ua/reyestr/",
        },
      ],
      confEyebrow: "Seminars · conferences",
      confLede:
        "Continuous education, supervision, and participation in international conferences. (Listed in the original Ukrainian.)",
      confItems: [
        "Theoretical-supervisory seminar, 3-4 July 2020, MD, psychiatrist, psychoanalyst, certified supervisor, Chair and associate professor at DGKB Ulrich Sachsse, 6 h.",
        "I International conference «Symboldrama: craft and art», 10-13 September 2020, MD, psychoanalyst, IGKIP associate and supervisor Harald Ullman, 30 h.",
        "International seminar «Confronting trauma with the symboldrama method», 12-13 March 2021, MD Ulrich Sachsse, 20 h.",
        "II International conference «Loving intention in symboldrama», 16-19 September 2021, PhD, IGKIP supervisor Traude Eberman, 30 h.",
        "Psychoanalytic online workshop with Nancy McWilliams, 18 December 2021, 10 h.",
        "XXVIII conference «Psychotherapist and war: a meeting at the edge of realities», USP, 18-19 June 2022, 16 h.",
        "III International conference «Symboldrama today: War and Life on the way to Peace», 15-18 September 2022, IGKIP associate Claudius Stein, 30 h.",
        "IX conference «War. Trauma. Identity», UKPP, 3-4 December 2022.",
        "«Working with personality disorders in clinical practice», Otto Kernberg, 27-28 January & 10-11 February 2023, 8 h.",
        "IV International conference «Time of revival: therapeutic possibilities of symboldrama», 14-17 September 2023, IGKIP associate Gudrun Hammon, 30 h.",
        "Seminar «Psychoanalysis of art and art in psychoanalysis», 22-26 June 2024, Vienna, Yulia Medynska & Olga Yaskevych, 30 h theory + 6 h supervision.",
        "«Psychosomatics in neurology», prof. Paul Janssen, prof. Ulrich Schulz-Venrath, 8-9 June 2024, 8 h.",
        "«Evidence-based practice in psychology and psychotherapy», 7 September 2024, Maryana Velykodna, 4 h.",
        "V International conference «Katathym-imaginative psychodynamic psychotherapy and the challenges of today», 12-15 September 2024, IGKIP associate Andrea Friedrichs-Dachale, 30 h.",
        "Seminar on Operationalised Psychodynamic Diagnostics OPD-3, 10-12 October 2024, 24 h.",
        "International conference «War and exhaustion. The psychology of resistance and recovery», UCU, 18-19 October 2024.",
        "Seminar «Symboldrama and contemporary psychoanalysis: a feminine perspective», 2-3 November 2024, IGKIP supervisor Traude Eberman, 12 h.",
        "«Personality disorders and psychosomatics», prof. Paul Janssen, dr. med. Katharina Martin, 8-9 March 2025, 8 h.",
        "«Family as a household guardian in the modern world», 6 September 2025, IRSGP associate, USP supervisor, systemic family therapist L.G. Dorofeyeva, 10 h.",
        "Seminars «Working with trauma in katathym-imaginative psychotherapy», DGKB associate and supervisor Beate Steiner, October 2025 — March 2026.",
      ],
      ctaTitleLead: "Ready to",
      ctaTitleAccent: "begin?",
      ctaBtn: "Book a consultation",
      pageTitle: "About",
    },
    consult: {
      eyebrow: "Booking",
      titleLead: "Nearest",
      titleAccent: "available hours.",
      body:
        "Pick a time that suits you — I confirm within a day. In Ukrainian or English. Kyiv · GMT+2.",
      cta: "Send a request",
      contactsEyebrow: "Contacts",
    },
    footer: {
      tag: "— Stay in quiet contact",
      titleLead: "Book a",
      titleAccent: "consultation.",
      cta: "Book",
      address: "Lviv, Ukraine",
      legal: { contacts: "Contacts", offer: "Public offer", rules: "Terms" },
      role: "Psychologist · Psychotherapist",
    },
    services: {
      eyebrow: "Services",
      titleLead: "Pricing and",
      titleAccent: "format.",
      lede:
        "The price reflects certification and experience and stays fixed for one calendar year — from January 1 to December 31.",
      tiersEyebrow: "What I offer",
      tiers: [
        {
          tag: "Standard",
          name: "Individual consultation",
          price: "2 000 ₴",
          dur: "50 min · online or in Lviv",
          d: "Consultation / psychotherapy in the symboldrama method. Format: online (Zoom, Google Meet) or in person at the office in the centre of Lviv.",
          inc: [
            "Symboldrama method",
            "Regular rhythm 1–2 times a week",
            "Online or in person",
          ],
          featured: true,
        },
        {
          tag: "Reduced",
          name: "Students, military, IDPs",
          price: "By arrangement",
          dur: "50 min · online or in Lviv",
          d: "Reduced-price consultation and psychotherapy for students, military service members and their families, and internally displaced persons.",
          inc: [
            "Same method and rhythm",
            "Reach out via Telegram or email",
            "Agreed individually",
          ],
        },
      ],
      whenEyebrow: "When to come",
      whenTitleLead: "Signs people",
      whenTitleAccent: "bring to therapy.",
      whenItems: [
        "Coping emotionally has become harder: shifts in mood, persistent irritability or sadness without clear cause; anxiety that interferes with daily life, apathy or low mood.",
        "A sense of emotional exhaustion, when energy is enough only to «get through».",
        "Bodily symptoms with no clear medical cause and/or that intensify under stress.",
        "A difficult period in relationships; relationships are draining your energy and worsening the quality of your life.",
        "Misunderstandings and conflicts with people close to you.",
        "Difficulty building and sustaining relationships.",
        "Loneliness that doesn't fade even among people.",
        "Feeling lost, unsure of your wishes and needs, frequent doubt in life choices.",
        "A sense that you're living someone else's life, frequent comparisons with others.",
        "Self-doubt, doubt in your achievements, feeling «not good enough».",
        "A sense that something is off, even though things look fine from the outside.",
        "Going through loss, life crisis, or existential questions.",
        "A wish to live more consciously and improve the quality of your life.",
      ],
      formatEyebrow: "Session format",
      formatTitleLead: "How a session",
      formatTitleAccent: "unfolds.",
      format: [
        { n: "01", t: "Beginning", d: "A short conversation: what has happened since, what brings you today." },
        { n: "02", t: "Image", d: "I offer a motif; you imagine; I gently accompany the experience." },
        { n: "03", t: "Conversation", d: "We return to words: what you noticed, what resonated, what opened up." },
        { n: "04", t: "Closing", d: "We secure what is important to take with you. We agree on the next meeting." },
      ],
      cancellationEyebrow: "Terms",
      cancellation:
        "Cancellations less than 24 hours before the meeting are charged in full. Earlier — we reschedule without payment.",
      faqEyebrow: "FAQ",
      faqTitleLead: "Frequently asked questions",
      faqTitleAccent: "",
      faqItems: [
        {
          q: "What kind of work do you offer?",
          a: "Consultation format: a few meetings to address current concerns; psychotherapy format: longer work for deeper and lasting change.",
        },
        {
          q: "How long and how often are the sessions?",
          a: "A consultation lasts 50 minutes, once or twice a week.",
        },
        {
          q: "What does the first meeting look like?",
          a: "The first meeting is a conversation in which we get to know each other and build contact, outline your request, and explore the context of your life. You can also use it to feel whether it is comfortable for you to work together.",
        },
        {
          q: "What are the payment options?",
          a: "Payment is made by the official bank details, either before or after the consultation.\n\nPayments from abroad can be made without commissions through the WayForPay payment system.",
        },
        {
          q: "What if I need to reschedule or cancel a meeting?",
          a: "Rescheduling or cancellation is possible if I am notified at least 24 hours in advance. Otherwise, the meeting is paid in full.\n\nIf the meeting is cancelled or rescheduled less than 24 hours in advance from my side as a therapist — your next meeting will be free.",
        },
        {
          q: "What if at some point I don't feel like coming to a session?",
          a: "Usually, behind that reluctance hide various emotions (anger, shame, guilt, helplessness) connected to the therapy process. An important part of psychotherapeutic work is naming and discussing them, rather than avoiding them.",
        },
        {
          q: "What if at some point I want to end therapy?",
          a: "It's worth setting aside 1 to 3 meetings to summarise the work and complete the collaboration.",
        },
      ],
      ctaBtn: "Book a consultation",
      pageTitle: "Services",
    },
    requests: {
      eyebrow: "Requests",
      titleLead: "When to come",
      titleAccent: "to a psychotherapist.",
      items: [
        "Coping emotionally has become harder: shifts in mood, persistent irritability or sadness without clear cause; anxiety that interferes with daily life, guilt, apathy or low mood.",
        "A sense of emotional exhaustion, when energy is enough only to “get through”.",
        "Bodily symptoms with no clear medical cause and/or that intensify under stress.",
        "A difficult period in relationships; relationships drain your energy and worsen the quality of your life.",
        "Misunderstandings and conflicts with people close to you.",
        "Difficulty building and sustaining relationships.",
        "Loneliness that does not fade even among people.",
        "Feeling lost, unsure of your wishes and needs, frequent doubt in life choices.",
        "A sense that you’re living someone else’s life, frequent comparisons with others.",
        "Self-doubt, doubt in your achievements, feeling “not good enough”.",
        "A sense that something is off, even though things look fine from the outside.",
        "Going through loss, life crisis, existential questions, change.",
        "A wish to live more consciously and improve the quality of your life.",
      ],
      ctaBtn: "Book a consultation",
      pageTitle: "Requests",
    },
    blog: {
      eyebrow: "Journal",
      titleLead: "Notes on",
      titleAccent: "the human psyche.",
      lede:
        "Writing on therapy, inner imagery, relationships — everything that walks beside us as we get to know ourselves.",
      fresh: "Latest",
      readMore: "Read",
      minutes: "min",
    },
    pageTitles: { blog: "Journal" },
  },
};